import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { History, X } from "lucide-react";
import LoadingIcon from "../../images/Loading_icon.gif";
import { UrlContext } from "../../contexts/UrlContext";

export default function ProposedRates({ expert_id, project_id }) {
  const [pastRates, setPastRates] = useState(null);
  const [showRates, setShowRates] = useState(false);
  const [ratesLoading, setRatesLoading] = useState(false);

  const buttonRef = useRef(null);
  const popupRef = useRef(null);

  const [popupPosition, setPopupPosition] = useState({
    top: 0,
    left: 0,
  });

  const { API_URL } = useContext(UrlContext);

  const positionPopup = () => {
    const button = buttonRef.current;
    const popup = popupRef.current;

    if (!button || !popup) return;

    const buttonRect = button.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();

    const viewportPadding = 12;
    const gap = 10;

    let left = buttonRect.right - popupRect.width;
    let top = buttonRect.top - popupRect.height - gap;

    left = Math.max(
      viewportPadding,
      Math.min(
        left,
        window.innerWidth - popupRect.width - viewportPadding
      )
    );

    if (top < viewportPadding) {
      top = viewportPadding;
    }

    setPopupPosition({
      top,
      left,
    });
  };

  const fetchProposedRates = () => {
    if (ratesLoading) return;

    setRatesLoading(true);

    const url =
      `${API_URL}fetch-proposed-rates/` +
      expert_id +
      "/" +
      project_id;

    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setPastRates(data.proposedRates);
        setShowRates(true);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setRatesLoading(false);
      });
  };

  const handleOpenRates = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (showRates) return;

    fetchProposedRates();
  };

  const handleCloseRates = () => {
    setShowRates(false);
  };

  useEffect(() => {
    if (!showRates) return;

    const frame = requestAnimationFrame(() => {
      positionPopup();
    });

    const handleViewportChange = () => {
      positionPopup();
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener(
        "scroll",
        handleViewportChange,
        true
      );
    };
  }, [showRates, pastRates]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedButton =
        buttonRef.current?.contains(event.target);

      const clickedPopup =
        popupRef.current?.contains(event.target);

      if (!clickedButton && !clickedPopup) {
        setShowRates(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  const popup =
    showRates && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={popupRef}
            className="
              fixed z-[10080]
              w-[340px] max-w-[calc(100vw-24px)]
              overflow-hidden
              rounded-xl
              border border-slate-200
              bg-white
              text-[#365561]
              shadow-[0_16px_40px_rgba(15,23,42,0.18)]
            "
            style={{
              top: popupPosition.top,
              left: popupPosition.left,
            }}
            data-pdp2-print-hide="true"
          >
            <div
              className="
                flex items-start justify-between gap-3
                border-b border-slate-100
                px-3.5 py-2.5
              "
            >
              <div className="min-w-0">
                <div className="text-[11px] font-semibold text-slate-800">
                  Previous proposed rates
                </div>

                <div className="mt-0.5 text-[9.5px] leading-[1.35] text-slate-400">
                  Historical rates for this consultant
                </div>
              </div>

              <button
                type="button"
                onClick={handleCloseRates}
                className="
                  flex h-6 w-6 shrink-0 items-center justify-center
                  rounded-full
                  border-0
                  bg-transparent
                  text-slate-400
                  transition
                  hover:bg-slate-100
                  hover:text-slate-700
                  focus:outline-none
                "
                aria-label="Close previous proposed rates"
                title="Close history"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>

            <div className="max-h-[180px] overflow-y-auto overflow-x-hidden p-2">
              {pastRates?.length > 0 ? (
                <div className="space-y-1.5">
                  {pastRates.map((rate, index) => (
                    <div
                      key={index}
                      className="
                        min-w-0
                        rounded-lg
                        bg-slate-50
                        px-3 py-2
                        text-[10.5px]
                        leading-[1.4]
                        text-[#365561]
                      "
                    >
                      <span className="block break-words [overflow-wrap:anywhere]">
                        {rate}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-3 py-6 text-center">
                  <div className="text-[10.5px] font-medium text-slate-500">
                    No previous rates available
                  </div>

                  <div className="mt-1 text-[9.5px] text-slate-400">
                    No historical proposed rate has been stored yet.
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div
        className="inline-flex items-center"
        data-pdp2-print-hide="true"
      >
        <button
          ref={buttonRef}
          type="button"
          onClick={handleOpenRates}
          disabled={ratesLoading}
          className="
            flex h-6 w-6 items-center justify-center
            rounded-full
            border border-white/30
            bg-white/10
            p-0
            text-white
            transition
            hover:bg-white/20
            focus:outline-none
            disabled:cursor-wait
            disabled:opacity-70
          "
          aria-label="Show previous proposed rates"
          aria-expanded={showRates}
          title="Previous proposed rates"
        >
          {ratesLoading ? (
            <img
              src={LoadingIcon}
              alt="Loading"
              className="h-[13px] w-[13px] object-contain"
            />
          ) : (
            <History size={14} strokeWidth={1.9} />
          )}
        </button>
      </div>

      {popup}
    </>
  );
}